import { Router, watchBusy } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers";
import type { PropertyName } from "@frui.ts/helpers";
import { computed } from "mobx";
import { Modules } from "config";
import type GatewayState from "entities/gatewayState";
import Region from "entities/region";
import GatewayResponse from "entities/gatewayResponse";
import Road from "entities/road";
import SearchGatewaysRequest from "entities/searchGatewaysRequest";
import Municipality from "entities/municipality";
import GatewayExternalIdentType from "entities/gatewayExternalIdentType";
import GatewayRepository from "repositories/gatewayRepository";
import LocalizationService from "services/localizationService";
import EventBus from "services/eventBus";
import ConfirmationService from "services/confirmationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import OptionsService from "services/optionsService";
import EnumService from "services/enumService";
import ListViewModel from "viewModels/listViewModel";

const navigationName = "gateways";

@Router.registerRoute({ name: Router.Self, route: navigationName })
export default class GatewaysViewModel extends ListViewModel<GatewayResponse> {
  readonly modelName = "gateway";
  protected abortController?: AbortController;

  constructor(
    protected repository: GatewayRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    protected securityService: SecurityService,
    protected optionsService: OptionsService,
    protected eventBus: EventBus,
    protected enumService: EnumService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get booleanOptions() {
    return [
      { id: true, label: this.localizationService.translateGeneral("boolean.true") },
      { id: false, label: this.localizationService.translateGeneral("boolean.false") },
    ];
  }

  protected createFilter(): SearchGatewaysRequest {
    return new SearchGatewaysRequest();
  }

  @bound
  @watchBusy
  public async loadData() {
    this.abortController = new AbortController();

    if (this.filter.externalGatewayIdent) {
      this.filter.externalGatewayIdent = this.filter.externalGatewayIdent.trim();
    }

    const result = await this.repository.getAll(this.filter, this.pagingFilter, this.abortController?.signal);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @bound
  allowItemDelete(item: GatewayResponse) {
    return false;
  }

  @bound
  regionName(regionId: string) {
    return this.optionsService.entityOptions(Region).find(region => region.id === regionId)?.label ?? "";
  }

  @bound
  municipalityName(municipalityId: string) {
    return this.optionsService.entityOptions(Municipality).find(municipality => municipality.id === municipalityId)?.label ?? "";
  }

  @computed
  get externalGatewayIdentOptions() {
    return this.optionsService.entityOptions(GatewayExternalIdentType).sort((a, b) => a.label.localeCompare(b.label));
  }

  @bound
  roadName(roadId: string) {
    return this.optionsService.entityOptions(Road).find(road => road.id === roadId)?.label ?? "";
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.enforcement, `gateways.${code}`, size);
  }

  translateState(state: GatewayState) {
    return this.localizationService.translateEnum("gatewayState", state);
  }
  translateFilterAttribute(attribute: PropertyName<SearchGatewaysRequest>, size?: number) {
    return this.localizationService.translateAttribute("gatewaysSearchRequest", attribute, size);
  }
}
